// src/components/Store/CartPanel.jsx
// Carrito + entrega + pago. Si el cliente tiene una reservación pagada que
// no ha terminado, los productos van con su perro (sin costo de envío); si
// no, pide dirección y cobra envío. El IVA se cobra siempre. El backend vuelve a calcular todo.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL, getAuthHeader } from '../../services/api';
import { fetchUserProfile } from '../../redux/slices/Users/userProfileSlice';
import { calculateOrderCharges, formatMXN, productName } from '../../utils/store';

const ADDRESS_FIELDS = ['nombre', 'paterno', 'direccion', 'colonia', 'delegacion', 'estado', 'zipcode', 'telefono'];
const REQUIRED_FIELDS = ['nombre', 'direccion', 'colonia', 'delegacion', 'estado', 'zipcode', 'telefono'];

const inputClass =
  'w-full px-3 py-2 border border-[#3A2A20]/15 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#9A0002]/40 focus:border-[#9A0002] transition';

const CartPanel = ({ cartItems, onChangeQty, onRemove }) => {
  const { t, i18n } = useTranslation('common');
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { profile } = useSelector((state) => state.userProfile);

  const [options, setOptions] = useState(null); // delivery-options del API
  const [optionsError, setOptionsError] = useState(false);
  const [address, setAddress] = useState(() => Object.fromEntries(ADDRESS_FIELDS.map((f) => [f, ''])));
  const [addressErrors, setAddressErrors] = useState({});
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!user) return;
    dispatch(fetchUserProfile());
    axios
      .get(`${API_BASE_URL}/orders/delivery-options`, { headers: getAuthHeader() })
      .then((res) => setOptions(res.data))
      .catch(() => setOptionsError(true));
  }, [user, dispatch]);

  // Pre-llena con la dirección del perfil (si tiene) sin pisar lo que ya escribió
  useEffect(() => {
    const saved = profile?.shippingAddress;
    if (!saved) return;
    setAddress((prev) =>
      Object.fromEntries(ADDRESS_FIELDS.map((f) => [f, prev[f] || saved[f] || '']))
    );
  }, [profile]);

  const subtotal = cartItems.reduce((sum, { product, quantity }) => sum + product.price * quantity, 0);
  const charges = calculateOrderCharges(subtotal, options);
  const withBooking = Boolean(options?.deliveryBooking);

  const validateAddress = () => {
    const errors = {};
    REQUIRED_FIELDS.forEach((f) => {
      if (!address[f]?.trim()) errors[f] = t('store.required');
    });
    if (address.zipcode && !/^\d{5}$/.test(address.zipcode.trim())) errors.zipcode = t('zipcode.mustBe5Digits');
    if (address.telefono && address.telefono.replace(/\D/g, '').length !== 10) errors.telefono = t('store.phoneInvalid');
    setAddressErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePay = async () => {
    if (!withBooking && !validateAddress()) return;
    setPaying(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/orders/checkout`,
        {
          items: cartItems.map(({ product, quantity }) => ({ product: product._id, quantity })),
          shippingAddress: withBooking ? undefined : address,
        },
        { headers: getAuthHeader() }
      );
      window.location.href = res.data.url; // Stripe Checkout
    } catch (error) {
      toast.error(t('store.checkoutFailed', { error: error.response?.data?.message || error.message }));
      setPaying(false);
    }
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'es-MX', { day: 'numeric', month: 'long' });

  if (cartItems.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-[#3A2A20]/10 p-6 text-center">
        <p className="text-sm text-[#3A2A20]/50">{t('store.cartEmpty')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-[#3A2A20]/10 p-5 space-y-5">

      {/* Productos */}
      <ul className="divide-y divide-[#3A2A20]/10">
        {cartItems.map(({ product, quantity, maxQty }) => (
          <li key={product._id} className="py-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#3A2A20] truncate">{productName(product, i18n.language)}</p>
              <p className="text-xs text-[#3A2A20]/50">{formatMXN(product.price)}</p>
            </div>
            <div className="flex items-center bg-[#9A0002]/5 rounded-lg">
              <button
                type="button"
                onClick={() => onChangeQty(product._id, -1)}
                aria-label={t('store.decrease')}
                className="w-7 h-7 text-[#9A0002] font-semibold hover:bg-[#9A0002]/10 rounded-lg"
              >
                −
              </button>
              <span className="w-6 text-center text-sm font-medium text-[#3A2A20]">{quantity}</span>
              <button
                type="button"
                onClick={() => onChangeQty(product._id, 1)}
                disabled={quantity >= maxQty}
                aria-label={t('store.increase')}
                className="w-7 h-7 text-[#9A0002] font-semibold hover:bg-[#9A0002]/10 rounded-lg disabled:opacity-30"
              >
                +
              </button>
            </div>
            <button
              type="button"
              onClick={() => onRemove(product._id)}
              className="text-xs text-[#3A2A20]/40 hover:text-[#9A0002]"
            >
              {t('store.remove')}
            </button>
          </li>
        ))}
      </ul>

      {/* Entrega */}
      {!user ? (
        <div className="bg-[#EFE6DD]/50 rounded-xl p-4 text-center space-y-3">
          <p className="text-sm text-[#3A2A20]/70">{t('store.loginToBuy')}</p>
          <Link
            to="/login"
            className="inline-block px-5 py-2.5 bg-[#9A0002] hover:bg-[#7A0001] text-[#EFE6DD] rounded-xl text-sm font-medium transition"
          >
            {t('navbar.signIn')}
          </Link>
        </div>
      ) : optionsError ? (
        <p className="text-sm text-[#9A0002]">{t('store.optionsError')}</p>
      ) : !options ? (
        <p className="text-sm text-[#3A2A20]/50">{t('common.loading')}</p>
      ) : withBooking ? (
        <div className="bg-[#3A6B4C]/10 rounded-xl p-4 text-sm text-[#3A6B4C]">
          {t('store.deliverWithBooking', { date: formatDate(options.deliveryBooking.checkOutDate) })}
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-[#3A2A20]">{t('store.shippingAddress')}</h3>
            <p className="text-xs text-[#3A2A20]/50 mt-0.5">{t('store.shippingNote')}</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {ADDRESS_FIELDS.map((field) => (
              <div key={field} className={['direccion', 'colonia'].includes(field) ? 'col-span-2' : ''}>
                <input
                  value={address[field]}
                  onChange={(e) => setAddress({ ...address, [field]: e.target.value })}
                  placeholder={field === 'delegacion' ? t('store.municipality') : t(`address.${field}`)}
                  aria-label={field === 'delegacion' ? t('store.municipality') : t(`address.${field}`)}
                  inputMode={field === 'zipcode' || field === 'telefono' ? 'numeric' : undefined}
                  maxLength={field === 'zipcode' ? 5 : 200}
                  className={inputClass}
                />
                {addressErrors[field] && <p className="text-[11px] text-[#9A0002] mt-0.5">{addressErrors[field]}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Totales */}
      <dl className="space-y-1.5 text-sm border-t border-[#3A2A20]/10 pt-4">
        <div className="flex justify-between text-[#3A2A20]/70">
          <dt>{t('store.subtotal')}</dt>
          <dd>{formatMXN(charges.subtotal)}</dd>
        </div>
        {/* Envío (gratis con reservación o arriba del mínimo) + IVA, que siempre se cobra */}
        {options && (
          <>
            <div className="flex justify-between text-[#3A2A20]/70">
              <dt>{t('store.shipping')}</dt>
              <dd>{charges.shippingCost === 0 ? t('store.free') : formatMXN(charges.shippingCost)}</dd>
            </div>
            {charges.shippingCost > 0 && options.freeShippingMin !== null && (
              <p className="text-[11px] text-[#3A6B4C]">
                {t('store.freeShippingHint', { amount: formatMXN(options.freeShippingMin - subtotal) })}
              </p>
            )}
            <div className="flex justify-between text-[#3A2A20]/70">
              <dt>{t('store.iva', { rate: Math.round(options.ivaRate * 100) })}</dt>
              <dd>{formatMXN(charges.iva)}</dd>
            </div>
          </>
        )}
        {!user && <p className="text-[11px] text-[#3A2A20]/50">{t('store.chargesAtCheckout')}</p>}
        <div className="flex justify-between font-semibold text-[#3A2A20] pt-1">
          <dt>{t('store.total')}</dt>
          <dd>{formatMXN(charges.total)} mxn</dd>
        </div>
      </dl>

      {user && (
        <button
          type="button"
          onClick={handlePay}
          disabled={paying || !options}
          className="w-full py-3 rounded-full font-semibold text-sm transition bg-[#9A0002] hover:bg-[#7A0001] text-[#EFE6DD] disabled:bg-[#3A2A20]/10 disabled:text-[#3A2A20]/40 disabled:cursor-not-allowed"
        >
          {paying ? t('bookingForm.processing') : t('store.pay', { amount: formatMXN(charges.total) })}
        </button>
      )}
    </div>
  );
};

export default CartPanel;
